import React, { useState } from "react";
import { CheckCircle2, Eye as PasswordEye, EyeOff, Loader2, Sparkles, XCircle } from "lucide-react";
import InstructorLayout from "../../layouts/InstructorLayout";
import {
  useGetInstructorMySlotsQuery,
  useGetInstructorProfileQuery,
  useVerifyInstructorTranscriptUsingAiMutation,
} from "../../services/instructor/instructor";
import { useNavigate } from "react-router-dom";
import {
  Calender,
  GraduationCap,
  LockIcon,
  OrangeProfileFilled,
} from "../../components/Icons";
import { Clock, ClockBlack, Eye } from "../../components/instructor/icons";
import { useGetPublicCoursesQuery } from "../../services/auth/authApi";
import { useChangePasswordMutation } from "../../services/user";
import Toast from "../../components/common/Toast";
import InstructorIdBadge from "../../components/instructor/InstructorIdBadge";
const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-2 xl:gap-2.5">
    {icon}
    <h3 className="text-lg xl:text-[22px] font-medium leading-[26px] text-black">
      {title}
    </h3>
  </div>
);
const InfoItem = ({ label, value, icon, url }) => {
  const handleOpenFile = () => {
    if (url) {
      window.open(url, "_blank");
    }
  };
  return (
    <div>
      <label className="font-medium leading-[26px] text-lg xl:text-[22px] text-[#484848]">
        {label}
      </label>

      <div className="flex items-center gap-4 mt-0.5">
        <p className="text-[#2D2D2D] text-base xl:text-xl leading-5 xl:leading-[23px]">
          {value}
        </p>

        {icon && (
          <button
            type="button"
            onClick={handleOpenFile}
            className="cursor-pointer"
          >
            {icon}
          </button>
        )}
      </div>
    </div>
  );
};

const getScoreLabel = (obtained, total) => {
  const obtainedValue = Number(obtained);
  const totalValue = Number(total);

  if (
    !Number.isNaN(obtainedValue) &&
    obtainedValue <= 5 &&
    (!total || Number.isNaN(totalValue) || totalValue <= 5)
  ) {
    return "CGPA";
  }

  return "Obtained Marks";
};

const MyProfile = () => {
  const navigate = useNavigate();
  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));

  const { data, isLoading } = useGetInstructorProfileQuery();

  const instructorProfile = data?.data?.[0] || [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const { data: publicCoursesResponse } = useGetPublicCoursesQuery();
  const publicCourses = publicCoursesResponse?.data ?? [];
  const courseIds = instructorProfile?.academicInfo?.coursePreferences || [];

  const preferredCourseNames = courseIds
    .map((id) => {
      const course = publicCourses.find((c) => c._id === id);
      return course?.courseTitle;
    })
    .filter(Boolean);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "success",
  });

  const [transcriptVerificationStatus, setTranscriptVerificationStatus] =
    useState(null);

  const [changePassword, { isLoading: isChangingPassword }] =
    useChangePasswordMutation();
  const [verifyTranscript, { isLoading: isVerifyingTranscript }] =
    useVerifyInstructorTranscriptUsingAiMutation();

  const handleVerifyTranscript = async () => {
    try {
      const response = await verifyTranscript().unwrap();
      const isVerified = Boolean(response?.data?.verification?.status);

      setTranscriptVerificationStatus(isVerified ? "verified" : "failed");
    } catch (error) {
      setToast({
        show: true,
        variant: "error",
        title: "Verification failed",
        message: error?.data?.message || "Could not verify transcript.",
      });
    }
  };

  const verifyButtonConfig = {
    verified: {
      className:
        "border-[#14AE5C] bg-[#14AE5C1A] text-[#14AE5C] hover:bg-[#14AE5C26]",
      icon: <CheckCircle2 size={14} />,
      label: "Verified",
      title: "Transcript verified successfully",
    },
    failed: {
      className:
        "border-[#EF4444] bg-[#EF44441A] text-[#EF4444] hover:bg-[#EF444426]",
      icon: <XCircle size={14} />,
      label: "Not Verified",
      title: "Transcript verification failed. Click to retry.",
    },
    idle: {
      className:
        "border-[#089BC4] text-[#089BC4] hover:bg-[#089BC41A]",
      icon: <Sparkles size={14} />,
      label: "Verify with AI",
      title: "Verify transcript with AI",
    },
  };

  const activeVerifyButton =
    verifyButtonConfig[transcriptVerificationStatus] ?? verifyButtonConfig.idle;

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdatePassword = async () => {
    setPasswordError("");

    const { currentPassword, newPassword, confirmNewPassword } = passwordData;

    // Empty validation
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setPasswordError("All fields are required");
      return;
    }

    // Match validation
    if (newPassword !== confirmNewPassword) {
      setPasswordError("New password and confirm password do not match");
      return;
    }

    try {
      const response = await changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword,
      }).unwrap();

      setShowPasswordForm(false);
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);

      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmNewPassword: "",
      });

      setToast({
        show: true,
        variant: "success",
        title: "Success",
        message: response?.message || "Password changed successfully.",
      });
    } catch (error) {
      setPasswordError(error?.data?.message || "Failed to update password");
    }
  };
  const { data: slotsResponse } = useGetInstructorMySlotsQuery();
  const activeSlots = slotsResponse?.data.filter((item) => item.isActive);
  const formatTime12h = (time24) => {
    if (!time24 || typeof time24 !== "string") return "";
    const [rawH, rawM] = time24.split(":");
    const hours = Number(rawH);
    const minutes = Number(rawM);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return time24;
    const period = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${h12}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  const weekDays = [
    { code: "MON", label: "Monday" },
    { code: "TUE", label: "Tuesday" },
    { code: "WED", label: "Wednesday" },
    { code: "THU", label: "Thursday" },
    { code: "FRI", label: "Friday" },
    { code: "SAT", label: "Saturday" },
    { code: "SUN", label: "Sunday" },
  ];

  const availabilityData = weekDays.map((d) => {
    const slots = (activeSlots || []).filter((slot) =>
      Array.isArray(slot?.days) ? slot.days.includes(d.code) : false,
    );

    if (slots.length === 0) {
      return { day: d.label, time: "", availability: false };
    }

    const time = slots
      .map(
        (slot) =>
          `${formatTime12h(slot?.startTime)} - ${formatTime12h(slot?.endTime)}`,
      )
      .filter(Boolean)
      .join(", ");

    return { day: d.label, time, availability: true };
  });

  const totalSlots = slotsResponse?.summary?.totalTimeSlots || 0;
  return (
    <InstructorLayout>
      <div className="px-3 sm:px-5 2xl:pl-6 2xl:pr-9">
        {isLoading ? (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className=" mt-1">
              <div className="bg-white border border-[#6055555E] px-4 sm:px-6 2xl:px-8 py-4 sm:py-5 2xl:py-6">
                <h1 className="mb-3 text-xl xl:text-[22px] text-black leading-[26px] font-medium">
                  My Profile
                </h1>
                <div className="bg-[#089BC412] px-5 sm:px-[26px] py-3 flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <img
                      src={instructorProfile?.profilePicture}
                      alt={loggedInUser?.firstName}
                      className="w-14 h-14 rounded-full object-cover"
                    />

                    <div>
                      <h2 className="text-[22px] xl:text-[26px] capitalize font-semibold leading-[26px]  text-[#6B6B6B]">
                        {instructorProfile?.name}
                      </h2>

                      <InstructorIdBadge
                        instructorId={
                          instructorProfile?.id || instructorProfile?._id
                        }
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("/instructor/profile/edit-profile")}
                    className="rounded-full bg-cyan-600 px-5 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700"
                  >
                    Edit Profile
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 2xl:gap-[22px] mt-4">
              <div className="p-4 sm:p-5 2xl:p-7 bg-white border border-[#60555557] rounded-xl">
                <SectionHeader
                  icon={<OrangeProfileFilled color="#089BC4" />}
                  title="Personal Information"
                />
                <div className="mt-4 mb-6 flex flex-wrap gap-5 2xl:gap-10 ">
                  <div className="space-y-6">
                    <InfoItem label="Email" value={instructorProfile?.email} />
                    <InfoItem
                      label="Address"
                      value={instructorProfile?.residenceInfo?.address}
                    />
                  </div>

                  <div className="space-y-6">
                    <InfoItem
                      label="Phone"
                      value={instructorProfile?.phoneNumber}
                    />
                    <InfoItem
                      label="Date of Birth"
                      value={formatDate(instructorProfile?.dateOfBirth)}
                    />
                  </div>
                </div>
                <InfoItem label="Bio" value={instructorProfile?.bio} />
              </div>
              <div className="p-4 sm:p-5 2xl:p-7 bg-white border border-[#60555557] rounded-xl">
                <div className="flex items-center justify-between gap-3">
                  <SectionHeader
                    icon={<GraduationCap color={"#089BC4"} />}
                    title="Academic Details"
                  />

                  <button
                    type="button"
                    onClick={handleVerifyTranscript}
                    disabled={
                      isVerifyingTranscript ||
                      !instructorProfile?.academicInfo?.transcript
                    }
                    title={activeVerifyButton.title}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 sm:px-3 sm:text-sm ${activeVerifyButton.className}`}
                  >
                    {isVerifyingTranscript ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      activeVerifyButton.icon
                    )}
                    <span className="hidden sm:inline">
                      {isVerifyingTranscript
                        ? "Verifying..."
                        : activeVerifyButton.label}
                    </span>
                  </button>
                </div>
                <div className="mt-4  grid grid-cols-2 gap-y-6">
                  <InfoItem
                    label="Qualification"
                    value={instructorProfile?.academicInfo?.qualification}
                  />
                  <InfoItem
                    label={getScoreLabel(
                      instructorProfile?.academicInfo?.obtainedMarks,
                      instructorProfile?.academicInfo?.totalMarks,
                    )}
                    value={instructorProfile?.academicInfo?.obtainedMarks}
                  />

                  <InfoItem
                    label="Degree Title"
                    value={instructorProfile?.academicInfo?.degreeTitle}
                  />
                  <InfoItem
                    label="Passing Year"
                    value={instructorProfile?.academicInfo?.graduationYear}
                  />

                  <InfoItem
                    label="Transcript"
                    value={"transcript"}
                    icon={<Eye />}
                    url={instructorProfile?.academicInfo?.transcript}
                  />
                  <InfoItem
                    label="Institute"
                    value={instructorProfile?.academicInfo?.institution}
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 2xl:gap-[22px]">
              <div>
                <div className="p-4 sm:p-5 2xl:p-7 bg-white border border-[#60555557] rounded-xl">
                  <SectionHeader
                    icon={<LockIcon color={"#089BC4"} />}
                    title="Teaching Course Preferences"
                  />

                  <label className="mt-4 block text-base xl:text-lg text-black leading-[21px]">
                    Preferred Courses*
                  </label>
                  <div className="flex flex-wrap gap-2 xl:gap-3 mt-2">
                    {preferredCourseNames.map((itm, idx) => (
                      <p className="text-black font-medium text-sm xl:text-base px-2.5 xl:px-3 py-1.5 xl:py-2 border border-[#43c7eb63] rounded-lg">
                        {itm}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="mt-4 p-4 sm:p-5 2xl:p-7 bg-white border border-[#60555557] rounded-xl">
                  <SectionHeader
                    icon={<LockIcon color={"#089BC4"} />}
                    title="Security Settings"
                  />
                  {!showPasswordForm ? (
                    <button
                      onClick={() => setShowPasswordForm(true)}
                      className="bg-[#089BC4] mt-6 xl:mt-8 py-1.5 xl:py-2.5 w-full rounded-lg font-medium text-lg xl:text-[22px] leading-[26px] text-[#FAFCFE]"
                    >
                      Change Password
                    </button>
                  ) : (
                    <div className="mt-5">
                      {passwordError && (
                        <div className="mb-4 bg-red-100 border border-red-300 text-red-600 px-4 py-2 rounded-lg">
                          {passwordError}
                        </div>
                      )}
                      <div>
                        <label className="block mb-[3px] leading-[19px] text-[#484848]">
                          Current Password
                        </label>
                        <input
                          type="text"
                          name="fakeUsername"
                          autoComplete="username"
                          className="hidden"
                        />
                        <div className="relative">
                          <input
                            type={showCurrentPassword ? "text" : "password"}
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            autoComplete="new-password"
                            placeholder="Enter current password"
                            className="w-full border border-[#D9D9D9] bg-[#F6F6F61C] rounded-[10px] px-4 py-1.5 pr-11 xl:py-[9px] outline-none focus:border-[#089BC4]"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowCurrentPassword((current) => !current)
                            }
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8CA1] hover:text-[#089BC4]"
                            aria-label={
                              showCurrentPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            {showCurrentPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <PasswordEye size={18} />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-y-[14px] xl:gap-y-0 xl:grid-cols-2 gap-x-4 xl:gap-x-6 mt-[14px]">
                        <div>
                          <label className="block mb-[3px]  leading-[19px] text-[#484848]">
                            New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              name="newPassword"
                              value={passwordData.newPassword}
                              onChange={handlePasswordChange}
                              autoComplete="new-password"
                              placeholder="Enter new password"
                              className="w-full border border-[#D9D9D9] bg-[#F6F6F61C] rounded-[10px] px-4 py-1.5 pr-11 xl:py-[9px] outline-none focus:border-[#089BC4]"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowNewPassword((current) => !current)
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8CA1] hover:text-[#089BC4]"
                              aria-label={
                                showNewPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                            >
                              {showNewPassword ? (
                                <EyeOff size={18} />
                              ) : (
                                <PasswordEye size={18} />
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Confirm New Password */}
                        <div>
                          <label className="block mb-[3px]  leading-[19px] text-[#484848]">
                            Confirm New Password
                          </label>
                          <div className="relative">
                            <input
                              type={
                                showConfirmNewPassword ? "text" : "password"
                              }
                              name="confirmNewPassword"
                              value={passwordData.confirmNewPassword}
                              onChange={handlePasswordChange}
                              autoComplete="new-password"
                              placeholder="Confirm new password"
                              className="w-full border border-[#D9D9D9] bg-[#F6F6F61C] rounded-[10px] px-4 py-1.5 pr-11 xl:py-[9px] outline-none focus:border-[#089BC4]"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmNewPassword((current) => !current)
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8CA1] hover:text-[#089BC4]"
                              aria-label={
                                showConfirmNewPassword
                                  ? "Hide password"
                                  : "Show password"
                              }
                            >
                              {showConfirmNewPassword ? (
                                <EyeOff size={18} />
                              ) : (
                                <PasswordEye size={18} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="flex justify-end gap-2 xl:gap-4 mt-8">
                        <button
                          onClick={() => {
                            setShowPasswordForm(false);
                            setShowCurrentPassword(false);
                            setShowNewPassword(false);
                            setShowConfirmNewPassword(false);
                            setPasswordError("");

                            setPasswordData({
                              currentPassword: "",
                              newPassword: "",
                              confirmNewPassword: "",
                            });
                          }}
                          className=" py-0.5 px-10 xl:px-14 bg-[#E3E3E3] text-sm xl:text-lg rounded xl:rounded-lg  text-[#676869] font-medium"
                        >
                          Cancel
                        </button>

                        <button
                          onClick={handleUpdatePassword}
                          disabled={isChangingPassword}
                          className="px-2.5 xl:px-[14px] py-0.5 rounded xl:rounded-lg text-sm xl:text-lg bg-[#089BC4] text-[#FAFCFE] font-medium"
                        >
                          {isChangingPassword
                            ? "Updating..."
                            : "Update Password"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 sm:p-5 2xl:p-7 bg-white border border-[#60555557] rounded-xl">
                <div className="flex items-center gap-6 xl:gap-10 ">
                  <SectionHeader icon={<Clock />} title="Availability" />
                  <p className="text-[#089BC4] hidden sm:block md:hidden lg:block rounded-full py-0.5 px-3 text-sm bg-[#089BC41A] border border-[#BABABA38]">
                    {totalSlots} Slots are available
                  </p>
                  <p className="text-[#089BC4] block sm:hidden md:block lg:hidden rounded-full py-0.5 px-3 text-sm bg-[#089BC41A] border border-[#BABABA38]">
                    {totalSlots} Slots
                  </p>
                </div>

                {/* Availability Rows */}
                <div className="mt-4 space-y-6 2xl:space-y-9">
                  {availabilityData.map((day) => (
                    <div
                      key={day.day}
                      className="flex flex-col sm:flex-row md:flex-col lg:flex-row items-center gap-2.5"
                    >
                      <div
                        className={`sm:max-w-[200px] md:max-w-full w-full  ${day.availability ? "bg-[#14AE5C1A] text-[#14AE5C]" : "bg-[#EF44441A] text-[#EF4444]"}  font-medium text-lg xl:text-[22px]  xl:py-[3px] border border-[#BABABA38] rounded-full flex items-center justify-center`}
                      >
                        {day.day}
                      </div>

                      <div className=" lg:max-w-[308px] w-full border border-[#089BC4] rounded-full xl:py-[3px] px-6 text-lg xl:text-[22px] font-['Inter']">
                        {day.availability ? (
                          <div className="flex items-center gap-2 xl:gap-2.5">
                            {" "}
                            <ClockBlack />
                            <span>{day.time}</span>
                          </div>
                        ) : (
                          <span className="text-center block">
                            No Availability
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {toast.show ? (
        <Toast
          variant={toast.variant}
          title={toast.title}
          message={toast.message}
          onClose={() => setToast((current) => ({ ...current, show: false }))}
        />
      ) : null}
    </InstructorLayout>
  );
};

export default MyProfile;
